import { sdk } from "@gc/ipecs-web-sdk";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useChannel } from "ably/react";
import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Icon, Label, Menu, Segment } from "semantic-ui-react";
import styled from "styled-components";
import PWA from "./components/PWA";
import config from "./config";
import { useAuth } from "./contexts/AuthContext";
import { CallDataProvider } from "./contexts/CallDataContext";
import { CallLogsProvider, useCallLogs } from "./contexts/CallLogsContext";
import { CallsProvider } from "./contexts/CallsContext";
import { ChatProvider } from "./contexts/ChatContext";
import { DevicesProvider, useDevices } from "./contexts/DevicesContext";
import { MyPresenceProvider } from "./contexts/MyPresenceContext";
import { MeetNowProvider } from "./components/primum/MeetNowButton";
import { PhoneControlProvider } from "./contexts/PhoneControlContext";
import { PresenceProvider } from "./contexts/PresenceContext";
import { SoftphoneProvider } from "./contexts/SoftphoneContext";
import { TopbarProvider } from "./contexts/TopbarContext";
import { UserClisProvider } from "./contexts/UserClisContext";
import useMessages from "./hooks/useMessages";
import sidekickLogo from "./images/sidekick-logo-75x79.png";
import { setLaunchNavigate } from "./launchHandler";
import layout from "./layout";
import devices from "./layout/devices";
import ApproveOrder from "./screens/ApproveOrder";
import Apps from "./screens/Apps";
import Calendar from "./screens/Calendar";
import Calls from "./screens/Calls";
import ChatRoom from "./screens/ChatRoom";
import Clockify from "./screens/Clockify";
import ConfigChecker from "./screens/ConfigChecker";
import Contacts from "./screens/Contacts";
import EngageChat from "./screens/EngageChat";
import Insights from "./screens/Insights";
import IpecsLogin from "./screens/IpecsLogin";
import Login from "./screens/Login";
import MakeCall from "./screens/MakeCall";
import Messages from "./screens/Messages";
import NotFound from "./screens/NotFound";
import Projects from "./screens/Projects";
import Settings from "./screens/Settings";
import Setup from "./screens/Setup";
import Sidekick from "./screens/Sidekick";
import SimpleMakeCall from "./screens/SimpleMakeCall";
import WhatsNew from "./screens/WhatsNew";
import WhatsappThread from "./screens/WhatsappThread";

const Sidebar = styled.div`
  overflow: hidden;
  min-width: ${layout.sidebarWidth};
  transition: all 100ms ease-in-out;
`;

const SidebarToggleWrapper = styled.div`
  display: block;
  position: absolute;
  left: 40px;
  top: 49px;
  zindex: 9999;
  cursor: pointer;
  color: #58b9b7;

  @media ${devices.tablet} {
    display: none;
  }
`;

const style = {
  wrapper: {
    minHeight: "100%",
  },
  app: {
    display: "flex",
  },
  sidebar: {
    height: `calc(100vh - ${layout.topbarHeight})`,
    overflowY: "auto",
    borderLeft: "none",
    borderTop: "none",
    borderBottom: "none",
    borderRadius: 0,
    minWidth: layout.sidebarWidth,
  },
  content: {
    width: "100%",
    height: `calc(100vh - ${layout.topbarHeight}`,
    overflowY: "scroll",
  },
};

const AppRoutes = () => {
  const { apiUser, ipecsUser, hasFeature, hasIntegration } = useAuth();

  const unauthenticatedRoutes = () => {
    return (
      <>
        <Route path="/" element={<Login />} />
        <Route path="/config-checker" element={<ConfigChecker />} />
        <Route path="*" element={<Navigate to="/" />} />
      </>
    );
  };

  const authenticatedWithoutIpecsRoutes = () => {
    return (
      <>
        <Route path="/" element={<IpecsLogin />} />
        <Route path="*" element={<IpecsLogin />} />
      </>
    );
  };

  const basicAuthenticatedRoutes = () => {
    return (
      <Route path="/" element={<BasicAuthLayout />}>
        <Route path="/simple-call/:number" element={<SimpleMakeCall />} />
      </Route>
    );
  };

  const authenticatedRoutes = () => {
    return (
      <Route path="/" element={<AuthLayout />}>
        <Route path="/pwa" element={<PWA />} />
        <Route index element={<Contacts />} />
        <Route path="/make-call/:number" element={<MakeCall />} />
        <Route
          path="/login/vincere/callback"
          Component={() => {
            window.location.href =
              config.api_url +
              "/login/vincere/callback" +
              window.location.search;
          }}
        />
        <Route path="/calls" element={<Calls />} />
        {hasIntegration(["microsoft", "google"]) && (
          <Route path="/calendar" element={<Calendar />} />
        )}
        {hasIntegration(["asana", "clickup"]) && (
          <Route path="/projects" element={<Projects />} />
        )}
        {hasIntegration(["clockify"]) && (
          <Route path="/clockify" element={<Clockify />} />
        )}
        {hasFeature(["insights"]) && (
          <Route path="/insights" element={<Insights />} />
        )}
        {apiUser?.has_sidekick && apiUser?.sidekick_configured && (
          <Route path="/sidekick" element={<Sidekick />} />
        )}
        <Route path="/apps" element={<Apps />} />
        <Route path="/settings/:action?" element={<Settings />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/messages" element={<MessagesRoute />} />
        <Route path="/messages/chat/:id" element={<ChatRoom />} />
        <Route path="/messages/whatsapp/:id" element={<WhatsappThread />} />
        <Route path="/messages/engage/:id" element={<EngageChat />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Wrapper />}>
          {!apiUser && unauthenticatedRoutes()}
          {apiUser && !ipecsUser && authenticatedWithoutIpecsRoutes()}
          {apiUser && ipecsUser && authenticatedRoutes()}
          {apiUser && ipecsUser && basicAuthenticatedRoutes()}
          <Route
            path="/partner/:billingPartnerId/orders/new"
            element={<Setup />}
          />
          <Route path="/order-approved" element={<ApproveOrder />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

function Wrapper() {
  const navigate = useNavigate();

  useEffect(() => {
    setLaunchNavigate(navigate);
    return () => setLaunchNavigate(null);
  }, [navigate]);

  return (
    <div style={style.wrapper}>
      <Outlet />
    </div>
  );
}

function BasicAuthLayout() {
  const { isPhoneControl } = useAuth();

  return (
    <DevicesProvider>
      <CallLogsProvider>
        <CallDataProvider>
          <MeetNowProvider>
            <CallHandler>
              <CallsProvider phoneControlMode={isPhoneControl}>
                <div>
                  <Outlet />
                </div>
              </CallsProvider>
            </CallHandler>
          </MeetNowProvider>
        </CallDataProvider>
      </CallLogsProvider>
    </DevicesProvider>
  );
}

function AuthLayout() {
  const { isPhoneControl, apiUser } = useAuth();
  const { user, setLogLevel } = sdk;

  useEffect(() => {
    if (Number.isInteger(apiUser.sdk_log_level)) {
      setLogLevel(apiUser.sdk_log_level);
    }
  }, [setLogLevel, apiUser]);

  useQuery({
    queryKey: ["auth-check"],
    queryFn: () => user.getMyInfo(),
    refetchOnWindowFocus: true,
    retry: 0,
    refetchInterval: 60000,
    cacheTime: 20000,
    staleTime: 20000,
    onError: () => window.location.reload(),
  });

  return (
    <MyPresenceProvider>
      <PresenceProvider>
        <DevicesProvider>
          <CallLogsProvider>
            <UserClisProvider>
              <CallDataProvider>
                <MeetNowProvider>
                  <CallHandler>
                    <CallsProvider phoneControlMode={isPhoneControl}>
                      <ChatProvider>
                        <AuthContent />
                      </ChatProvider>
                    </CallsProvider>
                  </CallHandler>
                </MeetNowProvider>
              </CallDataProvider>
            </UserClisProvider>
          </CallLogsProvider>
        </DevicesProvider>
      </PresenceProvider>
    </MyPresenceProvider>
  );
}

function CallHandler({ children }) {
  const { isPhoneControl } = useAuth();

  if (isPhoneControl) {
    return <PhoneControlProvider>{children}</PhoneControlProvider>;
  } else {
    return <SoftphoneProvider>{children}</SoftphoneProvider>;
  }
}

function AuthContent() {
  const { hasFeature, hasIntegration, apiUser, isPhoneControl } = useAuth();
  const { hasAnyMessageFeatures } = useMessages();
  const { missedCallCount } = useCallLogs();
  const { audioAllowed } = useDevices();
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setShowSidebar(
        window.innerWidth && window.innerWidth <= 540 ? false : true,
      );
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <TopbarProvider>
      <div style={style.app}>
        <SidebarToggleWrapper>
          <Icon
            name={showSidebar ? "chevron circle left" : "chevron circle right"}
            onClick={() => setShowSidebar(!showSidebar)}
          />
        </SidebarToggleWrapper>

        <Sidebar style={{ marginLeft: showSidebar ? 0 : "-100px" }}>
          <Menu
            inverted
            borderless
            icon="labeled"
            compact
            vertical
            style={style.sidebar}
          >
            <div style={{ flexGrow: "1" }}>
              <MenuItem to="/">
                <Icon name="address card" />
                Contacts
              </MenuItem>
              {hasAnyMessageFeatures && <MessagesMenuItem />}
              <MenuItem to="/calls">
                {missedCallCount > 0 && <AlertBadge count={missedCallCount} />}
                <Icon name="phone" />
                Calls
              </MenuItem>
              {hasIntegration(["microsoft", "google"]) && (
                <MenuItem to="/calendar">
                  <Icon name="calendar" />
                  Calendar
                </MenuItem>
              )}
              {hasIntegration(["asana", "clickup"]) && (
                <MenuItem to="/projects">
                  <Icon name="list" />
                  Projects
                </MenuItem>
              )}
              {hasIntegration(["clockify"]) && (
                <MenuItem to="/clockify">
                  <Icon name="clock" />
                  Clockify
                </MenuItem>
              )}
              {hasFeature(["insights"]) && (
                <MenuItem to="/insights">
                  <Icon name="chart line" />
                  Insights
                </MenuItem>
              )}
              {apiUser?.has_sidekick && apiUser?.sidekick_configured && (
                <MenuItem to="/sidekick">
                  <img
                    src={sidekickLogo}
                    alt="Sidekick logo"
                    align="center"
                    width="50px"
                    style={{ width: "25px", margin: "0px auto 6px auto" }}
                  />
                  Sidekick
                </MenuItem>
              )}
              <MenuItem to="/apps">
                <Icon name="clone" />
                Apps
              </MenuItem>
              <MenuItem to="/settings">
                <Icon name="settings" />
                Settings
              </MenuItem>
            </div>
          </Menu>
        </Sidebar>

        <div style={style.content}>
          {!isPhoneControl && !audioAllowed && (
            <Segment
              inverted
              color="red"
              style={{ borderRadius: 0, zIndex: 999 }}
            >
              Connect doesn't have permissions to audio devices. You will need
              to update your browser settings to allow Connect to use your audio
              devices.
            </Segment>
          )}

          <Outlet />
        </div>
      </div>
    </TopbarProvider>
  );
}

function AlertBadge({ count }) {
  return (
    <Label
      style={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 999,
      }}
      color="red"
    >
      {count}
    </Label>
  );
}

const MessagesRoute = () => {
  const { hasAnyMessageFeatures } = useMessages();

  if (!hasAnyMessageFeatures) {
    return <Navigate to="/" />;
  }

  return <Messages />;
};

function AblyChannelHandler({
  customerId,
  engageId,
  hasWhatsappFeature,
  hasEngageFeature,
}) {
  const queryClient = useQueryClient();

  useChannel(
    {
      channelName: `whatsapp:${customerId}:notifications`,
      skip: !hasWhatsappFeature,
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["whatsapp-threads"],
      });
    },
  );

  useChannel(
    {
      channelName: `chat:${engageId}:notifications`,
      skip: !hasEngageFeature,
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: ["engage-chats"],
      });
    },
  );

  return null;
}

function MessagesMenuItem() {
  const { apiUser } = useAuth();
  const { pathname } = useLocation();
  const { hasAnyUnreadMessages, hasEngageFeature, hasWhatsappFeature } =
    useMessages();

  const shouldShowNotification = useMemo(
    () => hasAnyUnreadMessages && !matchPath("/messages/*", pathname),
    [hasAnyUnreadMessages, pathname],
  );

  return (
    <MenuItem to="/messages">
      {apiUser?.has_ably && (
        <AblyChannelHandler
          customerId={apiUser.customer_id}
          engageId={apiUser.engage_id}
          hasWhatsappFeature={hasWhatsappFeature}
          hasEngageFeature={hasEngageFeature}
        />
      )}
      {shouldShowNotification && (
        <Label
          style={{
            position: "absolute",
            top: 12,
            right: 30,
            zIndex: 999,
          }}
          color="red"
          circular
          empty
          size="large"
        />
      )}
      <Icon name="inbox" /> Messages
    </MenuItem>
  );
}

function MenuItem({ to, children }) {
  const { pathname } = useLocation();

  return (
    <Menu.Item
      as={Link}
      to={to}
      active={matchPath(`${to}/*`, pathname) ? true : false}
    >
      {children}
    </Menu.Item>
  );
}

export default AppRoutes;
